import { FormlyFieldConfig } from "@ngx-formly/core";
import { map } from "rxjs";
import { SHM_CHANNEL_TYPES, MAX_SHM_CHANNELS } from "@cosmos/shared";

export class MeasurementForm {
    static transformArrayToMap(data: any) {
        if (data.dataChannels && Array.isArray(data.dataChannels)) {
            const channelsMap = {};
            data.dataChannels.forEach(c => {
              if (c.channelId) {
                channelsMap[`channel${c.channelId}`] = {
                  type: c.type,
                  parameters: c.parameters,
                  name: c.name
                };
              }
            });
            data.dataChannels = channelsMap;
        }
        return data;
    }

    commonFields: FormlyFieldConfig[];
    conditionalFields: { MaturityMeasurement: FormlyFieldConfig[], TemperatureMeasurement: FormlyFieldConfig[], StructuralHealthMeasurement: FormlyFieldConfig[] };

    fields: FormlyFieldConfig[];

    actionText = 'measurement.button';

    constructor(translateService, dataDevices, dataConcretes, user, config?) {

      const devices: {value: string, label: string, profile: string, parameters?: any}[] = [];
      for(const device of dataDevices) {
        devices.push({value: device['_id'], label: device['name'], profile: device.deviceProfile.class, parameters: device.deviceProfile.parameters });
      }

      const isAdminUser = user?.roles?.includes('admin') || user?.roles?.includes('superadmin');
      const barDiameters: number[] = config?.deviceProfile?.parameters?.corrosion?.barDiameters ?? [];
      const barLengths: number[] = config?.deviceProfile?.parameters?.corrosion?.barLengths ?? [];
      const diameterOptions = [
        ...barDiameters.map(d => ({ value: d, label: `${d} mm` })),
        ...(isAdminUser ? [{ value: 'custom', label: translateService.instant('fields.custom') }] : [])
      ];
      const lengthOptions = [
        ...barLengths.map(l => ({ value: l, label: `${l} mm` })),
        ...(isAdminUser ? [{ value: 'custom', label: translateService.instant('fields.custom') }] : [])
      ];
      const concretes: {value: string, label: string, specifications: number[], specifications_maturity: number[], specifications_maturity_relative: number[]}[] = [];
      for(const concrete of dataConcretes) concretes.push({value: concrete['_id'], label: `${concrete['name']} (${concrete.concreteMixture?.name || ''} - ${concrete.concreteMixture?.concreteType || ''})`, specifications: concrete['specifications'], specifications_maturity: concrete['specifications_maturity'], specifications_maturity_relative: concrete['specifications_maturity_relative'] });

      this.commonFields = [
          {
              key: 'name',
              type: 'input',
              wrappers: ['default-wrapper'],
              props: {
              required: true
              },
              expressions: {
                'props.label': translateService.stream('fields.name')
              }
          },
          {
              key: 'description',
              type: 'input',
              wrappers: ['default-wrapper'],
              props: {
              required: false
              },
              expressions: {
                'props.label': translateService.stream('fields.description')
              }
          },
          {
              key: 'device',
              type: 'select',
              wrappers: ['default-wrapper'],
              props: {
                required: true,
                options: devices,
                // description: 'El tipo de medición será acorde al dispositivo seleccionado'
              },
              expressions: {
                'props.label': translateService.stream('fields.device'),
              },
              hooks: {
              onInit: (field) => {
                  field.formControl.valueChanges.pipe(
                  map(value => {
                      const selectedDevice = devices.find(device => device.value === value);
                      const selectedProfile = selectedDevice.profile;
                      this.fields = this.commonFields.concat(this.conditionalFields[selectedProfile]);

                      const measurementTypeSelect = field.parent.get('measurementType')
                      measurementTypeSelect.props.options = [{
                        value: selectedProfile,
                        label: translateService.instant('measurement.types.' + selectedProfile)
                      }]
                      measurementTypeSelect.formControl.setValue(selectedProfile);

                      const diameters: number[] = selectedDevice.parameters?.corrosion?.barDiameters ?? [];
                      const lengths: number[] = selectedDevice.parameters?.corrosion?.barLengths ?? [];
                      const newDiameterOptions = [
                        ...diameters.map(d => ({ value: d, label: `${d} mm` })),
                        ...(isAdminUser ? [{ value: 'custom', label: translateService.instant('fields.custom') }] : [])
                      ];
                      const newLengthOptions = [
                        ...lengths.map(l => ({ value: l, label: `${l} mm` })),
                        ...(isAdminUser ? [{ value: 'custom', label: translateService.instant('fields.custom') }] : [])
                      ];
                      // Update options on the fieldArray config directly — Formly reads props by reference
                      const shmFieldGroup = this.conditionalFields.StructuralHealthMeasurement[0]?.fieldArray?.['fieldGroup'];
                      if (shmFieldGroup) {
                        const dCfg = shmFieldGroup.find((f: any) => f.key === 'parameters.diameter');
                        const lCfg = shmFieldGroup.find((f: any) => f.key === 'parameters.length');
                        if (dCfg) dCfg.props.options = newDiameterOptions;
                        if (lCfg) lCfg.props.options = newLengthOptions;
                      }
                  })
                  ).subscribe()
              }
              }
          },
          {
            key: 'measurementType',
            type: 'select',
            wrappers: ['default-wrapper'],
            props: {
              required: true,
              disabled: true
            },
            expressions: {
              'props.label': translateService.stream('fields.measurementType'),
              hide: '!model.device'
            }
        },
        {
          key: 'started',
          type: 'checkbox',
          wrappers: ['checkbox-wrapper'],
          defaultValue: config?.defaultMeasurementStarted ?? true,
          props: {
            required: false,
          },
          expressions: {
            'props.label': translateService.stream('fields.started'),
            hide: (field) => !['MaturityMeasurement', 'TemperatureMeasurement'].includes(field.model?.measurementType),
          },
        },
      ]

      this.conditionalFields = {
          MaturityMeasurement: [
            {
              key: 'concrete',
              type: 'select',
              wrappers: ['default-wrapper'],
              props: {
                options: concretes,
                required: true
              },
              expressions: {
                'props.label': translateService.stream('fields.concrete')
              },
              hooks: {
                onInit: (field) => {
                  field.formControl.valueChanges.pipe(
                    map(value => {
                      const concrete = concretes.find(concrete => concrete.value === value);
                      field.parent.get('specifications').formControl.setValue(concrete.specifications);
                      if(field.parent.get('specifications_maturity'))field.parent.get('specifications_maturity').formControl.setValue(concrete.specifications_maturity);
                      if(field.parent.get('specifications_maturity_relative'))field.parent.get('specifications_maturity_relative').formControl.setValue(concrete.specifications_maturity_relative);
                    })
                  ).subscribe()
                }
              }
            },
            {
              key: 'specifications',
              type: 'chips-number',
              wrappers: ['default-wrapper'],
              props: {
                type: 'number',
                required: false,
                suffix: ' MPa'
              },
              expressions: {
                'props.label': translateService.stream('fields.specifications')
              }
            },
            {
              key: 'specifications_maturity',
              type: 'chips-number',
              wrappers: ['default-wrapper'],
              hide: !config.visualizationConfig?.maturity.app,
              props: {
                type: 'number',
                required: false,
                suffix: ' ºC d',
              },
              expressions: {
                'props.label': translateService.stream('fields.specifications_maturity'),
              }
            },
            {
              key: 'specifications_maturity_relative',
              type: 'chips-number',
              wrappers: ['default-wrapper'],
              hide: !config.visualizationConfig?.relativeMaturity.app,
              props: {
                type: 'number',
                required: false,
                suffix: ' ºC d'
              },
              expressions: {
                'props.label': translateService.stream('fields.specifications_maturity_relative')
              }
            },
          ],
          TemperatureMeasurement: [],
          StructuralHealthMeasurement: [
            {
              key: 'dataChannels',
              type: 'shm-channels',
              defaultValue: [{ channelId: 1, type: 'None', parameters: {} }],
              props: { label: 'fields.dataChannels' },
              fieldArray: {
                fieldGroup: [
                  {
                    key: 'channelId',
                    type: 'select',
                    props: {
                      label: translateService.instant('fields.channel'),
                      required: true,
                    },
                    wrappers: ['default-wrapper'],
                    expressions: {
                      'props.options': (field: any) => {
                        const parentArray = (field.parent?.parent?.model || []);
                        const rowModel = field.model;
                        if (!Array.isArray(parentArray)) return [];

                        const currentIndex = parentArray.indexOf(rowModel);
                        const usedChannels = parentArray
                          .map((item, idx) => (item && idx !== currentIndex) ? item.channelId : null)
                          .filter(id => id !== null);

                        return Array.from({ length: MAX_SHM_CHANNELS }, (_, i) => i + 1)
                          .filter(c => !usedChannels.includes(c) || c === rowModel?.channelId)
                          .map(c => ({
                            value: c,
                            label: `${translateService.instant('fields.channel')} ${c}`
                          }));
                      }
                    }
                  },
                  {
                    // Optional display label — charts / CSV fall back to the
                    // channel number when blank. Shown for every channel type.
                    key: 'name',
                    type: 'input',
                    wrappers: ['default-wrapper'],
                    props: { required: false },
                    expressions: {
                      'props.label': translateService.stream('fields.channelName'),
                    }
                  },
                  {
                    key: 'type',
                    type: 'select',
                    wrappers: ['default-wrapper'],
                    props: {
                      label: translateService.instant('fields.type'),
                      required: true,
                      options: SHM_CHANNEL_TYPES.map(t => ({ value: t.value, label: translateService.instant(t.label) }))
                    }
                  },
                  {
                    key: 'parameters.diameter',
                    type: 'select',
                    wrappers: ['default-wrapper'],
                    props: {
                      required: true,
                      options: diameterOptions
                    },
                    expressions: {
                      'props.label': translateService.stream('fields.diameter'),
                      hide: (field) => field.model?.type !== 'corrosion'
                    }
                  },
                  // Rebar cover + design strength — inputs to the thickloss
                  // structural-limit threshold band. Optional: the band simply
                  // isn't drawn when either is absent.
                  {
                    key: 'parameters.cover',
                    type: 'input',
                    wrappers: ['default-wrapper'],
                    props: {
                      type: 'number',
                      required: false
                    },
                    expressions: {
                      'props.label': translateService.stream('fields.cover'),
                      hide: (field) => field.model?.type !== 'corrosion'
                    }
                  },
                  {
                    key: 'parameters.fck',
                    type: 'input',
                    wrappers: ['default-wrapper'],
                    props: {
                      type: 'number',
                      required: false
                    },
                    expressions: {
                      'props.label': translateService.stream('fields.fck'),
                      hide: (field) => field.model?.type !== 'corrosion'
                    }
                  },
                  {
                    key: 'parameters.length',
                    type: 'select',
                    wrappers: ['default-wrapper'],
                    props: {
                      required: true,
                      options: lengthOptions
                    },
                    expressions: {
                      'props.label': translateService.stream('fields.length'),
                      hide: (field) => field.model?.type !== 'corrosion'
                    }
                  },
                  {
                    key: 'parameters.diameterCustom',
                    type: 'input',
                    wrappers: ['default-wrapper'],
                    props: {
                      type: 'number',
                      required: true
                    },
                    expressions: {
                      'props.label': translateService.stream('fields.diameterCustom'),
                      hide: (field) => field.model?.type !== 'corrosion' || field.model?.parameters?.diameter !== 'custom'
                    }
                  },
                  {
                    key: 'parameters.lengthCustom',
                    type: 'input',
                    wrappers: ['default-wrapper'],
                    props: {
                      type: 'number',
                      required: true
                    },
                    expressions: {
                      'props.label': translateService.stream('fields.lengthCustom'),
                      hide: (field) => field.model?.type !== 'corrosion' || field.model?.parameters?.length !== 'custom'
                    }
                  }
                ]
              }
            }
          ]
        }
      this.fields = this.commonFields;
    }
}

export class EditMeasurementForm {
  commonFields: FormlyFieldConfig[];
  // conditionalFields: {MaturityMeasurement: FormlyFieldConfig[], TemperatureMeasurement: FormlyFieldConfig[]};

  fields: FormlyFieldConfig[];
  model: {};
  actionText = 'button.save';

  constructor(translateService, measurement, user, config?) {
    this.model = measurement;

    const isAdminUser = user?.roles?.includes('admin') || user?.roles?.includes('superadmin');
    const barDiameters: number[] = config?.deviceProfile?.parameters?.corrosion?.barDiameters ?? [];
    const barLengths: number[] = config?.deviceProfile?.parameters?.corrosion?.barLengths ?? [];
    const diameterOptions = [
      ...barDiameters.map(d => ({ value: d, label: `${d} mm` })),
      ...(isAdminUser ? [{ value: 'custom', label: translateService.instant('fields.custom') }] : [])
    ];
    const lengthOptions = [
      ...barLengths.map(l => ({ value: l, label: `${l} mm` })),
      ...(isAdminUser ? [{ value: 'custom', label: translateService.instant('fields.custom') }] : [])
    ];

    if (config?.measurementType === 'StructuralHealthMeasurement') {
      if (this.model['dataChannels'] && typeof this.model['dataChannels'] === 'object' && !Array.isArray(this.model['dataChannels'])) {
        this.model['dataChannels'] = Object.keys(this.model['dataChannels']).map(key => {
          const channelId = parseInt(key.replace('channel', ''));
          return {
            channelId,
            ...this.model['dataChannels'][key]
          };
        });
      } else if (!this.model['dataChannels']) {
        this.model['dataChannels'] = [{ channelId: 1, type: 'None', parameters: {} }];
      }
    }

    this.commonFields = [
      {
        key: 'name',
        type: 'input',
        wrappers: ['default-wrapper'],
        defaultValue: measurement.name,
        props: {
          required: true,
        },
        expressions: {
          'props.label': translateService.stream('fields.name')
        }
      },
      {
        key: 'description',
        type: 'input',
        wrappers: ['default-wrapper'],
        defaultValue: measurement.description,
        props: {
          required: false
        },
        expressions: {
          'props.label': translateService.stream('fields.description')
        }
      },
      {
        key: 'measurementType',
        type: 'select',
        wrappers: ['default-wrapper'],
        defaultValue: measurement.measurementType,
        props: {
          required: true,
          disabled: true,
          options: [{value: config.measurementType, label: translateService.instant('measurement.types.' + config.measurementType)}]
        },
        expressions: {
          'props.label': translateService.stream('fields.measurementType'),
        }
      },
      {
        key: 'started',
        type: 'checkbox',
        wrappers: ['checkbox-wrapper'],
        defaultValue: measurement.started !== false,
        hide: !(config.measurementType === 'MaturityMeasurement' || config.measurementType === 'TemperatureMeasurement'),
        props: {
          required: false,
        },
        expressions: {
          'props.label': translateService.stream('fields.started'),
        }
      },
      {
        key: 'specifications',
        type: 'chips-number',
        wrappers: ['default-wrapper'],
        defaultValue: measurement.specifications,
        hide: !(config.measurementType === 'MaturityMeasurement'),
        props: {
          type: 'number',
          required: false,
          suffix: ' MPa'
        },
        expressions: {
          'props.label': translateService.stream('fields.specifications')
        }
      },
      {
        key: 'specifications_maturity',
        type: 'chips-number',
        wrappers: ['default-wrapper'],
        defaultValue: measurement.specifications_maturity,
        hide: !((config.measurementType === 'MaturityMeasurement') && config.visualizationConfig?.maturity.app),
        props: {
          type: 'number',
          required: false,
          suffix: ' ºC d',
        },
        expressions: {
          'props.label': translateService.stream('fields.specifications_maturity'),
        }
      },
      {
        key: 'specifications_maturity_relative',
        type: 'chips-number',
        wrappers: ['default-wrapper'],
        defaultValue: measurement.specifications_maturity_relative,
        hide: !((config.measurementType === 'MaturityMeasurement') && config.visualizationConfig?.relativeMaturity.app),
        props: {
          type: 'number',
          required: false,
          suffix: ' ºC d'
        },
        expressions: {
          'props.label': translateService.stream('fields.specifications_maturity_relative')
        }
      },
      {
        key: 'dataChannels',
        type: 'shm-channels',
        defaultValue: (measurement.dataChannels && measurement.dataChannels.length > 0)
          ? measurement.dataChannels
          : [{ channelId: 1, type: 'None', parameters: {} }],
        hide: !(config.measurementType === 'StructuralHealthMeasurement'),
        props: { label: translateService.instant('fields.dataChannels') },
        fieldArray: {
          fieldGroup: [
            {
              key: 'channelId',
              type: 'select',
              props: {
                label: translateService.instant('fields.channel'),
                required: true,
              },
              wrappers: ['default-wrapper'],
              expressions: {
                'props.options': (field: any) => {
                  const parentArray = (field.parent?.parent?.model || []);
                  const rowModel = field.model;
                  if (!Array.isArray(parentArray)) return [];

                  const currentIndex = parentArray.indexOf(rowModel);
                  const usedChannels = parentArray
                    .map((item, idx) => (item && idx !== currentIndex) ? item.channelId : null)
                    .filter(id => id !== null);

                  return Array.from({ length: MAX_SHM_CHANNELS }, (_, i) => i + 1)
                    .filter(c => !usedChannels.includes(c) || c === rowModel?.channelId)
                    .map(c => ({
                      value: c,
                      label: `${translateService.instant('fields.channel')} ${c}`
                    }));
                }
              }
            },
            {
              // Optional display label — charts / CSV fall back to the channel
              // number when blank. Shown for every channel type.
              key: 'name',
              type: 'input',
              wrappers: ['default-wrapper'],
              props: { required: false },
              expressions: {
                'props.label': translateService.stream('fields.channelName'),
              }
            },
            {
              key: 'type',
              type: 'select',
              wrappers: ['default-wrapper'],
              props: {
                label: translateService.instant('fields.type'),
                required: true,
                options: SHM_CHANNEL_TYPES.map(t => ({ value: t.value, label: translateService.instant(t.label) }))
              }
            },
            {
              key: 'parameters.diameter',
              type: 'select',
              wrappers: ['default-wrapper'],
              props: {
                required: true,
                options: diameterOptions
              },
              expressions: {
                'props.label': translateService.stream('fields.diameter'),
                hide: (field) => field.model?.type !== 'corrosion'
              }
            },
            // Rebar cover + design strength — inputs to the thickloss
            // structural-limit threshold band. Optional: the band simply
            // isn't drawn when either is absent.
            {
              key: 'parameters.cover',
              type: 'input',
              wrappers: ['default-wrapper'],
              props: {
                type: 'number',
                required: false
              },
              expressions: {
                'props.label': translateService.stream('fields.cover'),
                hide: (field) => field.model?.type !== 'corrosion'
              }
            },
            {
              key: 'parameters.fck',
              type: 'input',
              wrappers: ['default-wrapper'],
              props: {
                type: 'number',
                required: false
              },
              expressions: {
                'props.label': translateService.stream('fields.fck'),
                hide: (field) => field.model?.type !== 'corrosion'
              }
            },
            {
              key: 'parameters.length',
              type: 'select',
              wrappers: ['default-wrapper'],
              props: {
                required: true,
                options: lengthOptions
              },
              expressions: {
                'props.label': translateService.stream('fields.length'),
                hide: (field) => field.model?.type !== 'corrosion'
              }
            },
            {
              key: 'parameters.diameterCustom',
              type: 'input',
              wrappers: ['default-wrapper'],
              props: {
                type: 'number',
                required: true
              },
              expressions: {
                'props.label': translateService.stream('fields.diameterCustom'),
                hide: (field) => field.model?.type !== 'corrosion' || field.model?.parameters?.diameter !== 'custom'
              }
            },
            {
              key: 'parameters.lengthCustom',
              type: 'input',
              wrappers: ['default-wrapper'],
              props: {
                type: 'number',
                required: true
              },
              expressions: {
                'props.label': translateService.stream('fields.lengthCustom'),
                hide: (field) => field.model?.type !== 'corrosion' || field.model?.parameters?.length !== 'custom'
              }
            }
          ]
        }
      },
    ];
    this.fields = this.commonFields;
  }
}

export class MeasurementChangeDeviceForm {
  fields: FormlyFieldConfig[];

  actionText = 'measurement.change_device';

  constructor(translateService, dataDevices, user, config?) {

    const devices: {value: string, label: string, profile: string}[] = [];
    for(const device of dataDevices) devices.push({value: device['_id'], label: device['name'], profile: device.deviceProfile.class });

    this.fields = [
      {
        key: 'device',
        type: 'select',
        wrappers: ['default-wrapper'],
        props: {
          required: true,
          options: devices,
          // description: 'El tipo de medición será acorde al dispositivo seleccionado'
        },
        expressions: {
          'props.label': translateService.stream('fields.device'),
        },
      }
    ]
  }

}

export class MeasurementRescheduleForm {
  fields: FormlyFieldConfig[];

  actionText = 'measurement.reschedule.button';

  constructor(translateService, dataTimestamps, startTimestamp, endTimestamp, user, config?) {

    const timestamps: {value: string, label: string}[] = [];
    for(const [index, timestamp] of dataTimestamps.entries()) {
      timestamps.push({value: index, label: new Date(timestamp).toLocaleString()})
    };

    this.fields = [
      {
        key: 'timestampStart',
        type: 'select',
        wrappers: ['default-wrapper'],
        defaultValue: startTimestamp,
        props: {
          required: true,
          options: timestamps,
        },
        expressions: {
          'props.label': translateService.stream('fields.timestampStart'),
        },
      },
      {
        key: 'timestampEnd',
        type: 'select',
        wrappers: ['default-wrapper'],
        defaultValue: endTimestamp,
        props: {
          required: true,
          options: timestamps,
        },
        expressions: {
          'props.label': translateService.stream('fields.timestampEnd'),
        },
      }
    ]
  }

}

export class MeasurementStartForm {
  fields: FormlyFieldConfig[];

  actionText = 'measurement.start.button';

  constructor(translateService, dataTimestamps, defaultStartTimestamp, user, config?) {

    const timestamps: {value: string, label: string}[] = [];
    for(const [index, timestamp] of dataTimestamps.entries()) {
      timestamps.push({value: index, label: new Date(timestamp).toLocaleString()})
    };

    this.fields = [
      {
        key: 'timestampStart',
        type: 'select',
        wrappers: ['default-wrapper'],
        defaultValue: defaultStartTimestamp,
        props: {
          required: true,
          options: timestamps,
        },
        expressions: {
          'props.label': translateService.stream('fields.timestampStart'),
        },
      }
    ]
  }
}

export class AdminMeasurementForm {

  commonFields: FormlyFieldConfig[];
  conditionalFields?: { MaturityMeasurement: FormlyFieldConfig[], TemperatureMeasurement: FormlyFieldConfig[], StructuralHealthMeasurement: FormlyFieldConfig[] };

  fields: FormlyFieldConfig[];

  actionText = 'measurement.button';

  constructor(translateService, stagesService, projectsService, devicesService, concretesService, dataProjects, user, config?) {

    const isAdminUser = user?.roles?.includes('admin') || user?.roles?.includes('superadmin');
    const barDiameters: number[] = config?.deviceProfile?.parameters?.corrosion?.barDiameters ?? [];
    const barLengths: number[] = config?.deviceProfile?.parameters?.corrosion?.barLengths ?? [];
    const diameterOptions = [
      ...barDiameters.map(d => ({ value: d, label: `${d} mm` })),
      ...(isAdminUser ? [{ value: 'custom', label: translateService.instant('fields.custom') }] : [])
    ];
    const lengthOptions = [
      ...barLengths.map(l => ({ value: l, label: `${l} mm` })),
      ...(isAdminUser ? [{ value: 'custom', label: translateService.instant('fields.custom') }] : [])
    ];

    const projects: {value: string, label: string}[] = [];
    for(const project of dataProjects) projects.push({value: project['_id'], label: project['name']});

    this.commonFields = [
        {
            key: 'name',
            type: 'input',
            wrappers: ['default-wrapper'],
            props: {
            required: true
            },
            expressions: {
              'props.label': translateService.stream('fields.name')
            }
        },
        {
            key: 'description',
            type: 'input',
            wrappers: ['default-wrapper'],
            props: {
            required: false
            },
            expressions: {
              'props.label': translateService.stream('fields.description')
            }
        },
        {
          key: 'company',
          type: 'select',
          wrappers: ['default-wrapper'],
          props: {
            required: true,
            disabled: true
          },
          expressions: {
            'props.label': translateService.stream('fields.company'),
            hide: '!model.project'
          }
        },
        {
          key: 'owner',
          type: 'select',
          wrappers: ['default-wrapper'],
          props: {
            required: true,
            disabled: true

          },
          expressions: {
            'props.label': translateService.stream('fields.owner'),
            hide: '!model.project'
          },
        },
        {
          key: 'project',
          type: 'select',
          wrappers: ['default-wrapper'],
          props: {
            required: true,
            options: projects
          },
          expressions: {
            'props.label': translateService.stream('fields.project'),
          },
          hooks: {
            onInit: (field) => {
                field.formControl.valueChanges.pipe(
                map(value => {
                    const selectedProject = field.formControl.value;
                    stagesService.find('?project='+ selectedProject).subscribe({
                      next: (response) => {
                        const stages: {value: string, label: string}[] = [];
                        for(const stage of response.body) stages.push({value: stage['_id'], label: stage['name'] });
                        const stageField = field.parent.get('stage');
                        stageField.props.options = stages;
                        stageField.formControl.setValue(null);
                      },
                      error: (e) => {} // ADD TOAST e),
                    });

                    devicesService.find(`?project=${selectedProject}&available=true&populate_deviceProfile=class`).subscribe({
                      next: (response) => {
                        const devices: {value: string, label: string, profile: string}[] = [];
                        for(const device of response.body) devices.push({value: device['_id'], label: device['name'], profile: device['deviceProfile']['class'] });
                        const deviceField = field.parent.get('device');
                        deviceField.props.options = devices;
                        deviceField.formControl.setValue(null);
                      },
                      error: (e) => {} // ADD TOAST e),
                    });

                    projectsService.findById(selectedProject, '?populate_owner=name&populate_company=name').subscribe({
                      next: (response) => {
                        const companyField = field.parent.get('company');
                        companyField.props.options = [{value: response.body.company._id, label: response.body.company.name}];
                        companyField.formControl.setValue(response.body.company._id);
                        const ownerField = field.parent.get('owner');
                        ownerField.props.options = [{value: response.body.owner._id, label: response.body.owner.name}];
                        ownerField.formControl.setValue(response.body.owner._id);
                      },
                      error: (e) => {} // ADD TOAST e),
                    });

                    projectsService.getMonitoringConfig(selectedProject).subscribe({
                      next: (response) => {
                        const startedField = field.parent.get('started');
                        if (startedField) {
                          startedField.formControl.setValue(response.body?.defaultMeasurementStarted ?? true);
                        }
                      },
                      error: (e) => {} // ADD TOAST e),
                    });
                })
                ).subscribe()
            }
            }
        },
        {
          key: 'stage',
          type: 'select',
          wrappers: ['default-wrapper'],
          props: {
            required: true,
          },
          expressions: {
            'props.label': translateService.stream('fields.stage'),
            hide: '!model.project'
          },
        },
        {
            key: 'device',
            type: 'select',
            wrappers: ['default-wrapper'],
            props: {
              required: true,
              // description: 'El tipo de medición será acorde al dispositivo seleccionado'
            },
            expressions: {
              'props.label': translateService.stream('fields.device'),
              hide: '!model.project'
            },
            hooks: {
            onInit: (field) => {
                field.formControl.valueChanges.pipe(
                map(value => {
                  if(value!=null) {
                    const selectedProfile = (field.props.options as any[]).find(device => device.value === value).profile;
                    this.fields = this.commonFields.concat(this.conditionalFields[selectedProfile]);
                    
                    const measurementTypeSelect = field.parent.get('measurementType')
                    measurementTypeSelect.props.options = [{
                      value: selectedProfile,
                      label: translateService.instant('measurement.types.' + selectedProfile)
                    }]
                    measurementTypeSelect.formControl.setValue(selectedProfile);
                  }
                })
                ).subscribe()
            }
            }
        },
        {
          key: 'measurementType',
          type: 'select',
          wrappers: ['default-wrapper'],
          props: {
            required: true,
            disabled: true
          },
          expressions: {
            'props.label': translateService.stream('fields.measurementType'),
            hide: '!model.device'
          }
        },
        {
          key: 'started',
          type: 'checkbox',
          wrappers: ['checkbox-wrapper'],
          defaultValue: true,
          props: {
            required: false,
          },
          expressions: {
            'props.label': translateService.stream('fields.started'),
            hide: (field) => !['MaturityMeasurement', 'TemperatureMeasurement'].includes(field.model?.measurementType),
          },
        },
    ]

    this.conditionalFields = {
        MaturityMeasurement: [
          {
            key: 'concrete',
            type: 'select',
            wrappers: ['default-wrapper'],
            props: {
              required: true
            },
            expressions: {
              'props.label': translateService.stream('fields.concrete')
            },
            hooks: {
              onInit: (field) => {
                concretesService.find(`?project=${field.form.value.project}`).subscribe({
                  next: (response) => {
                    const concretes: {value: string, label: string, specifications: number[], specifications_maturity: number[], specifications_maturity_relative: number[]}[] = [];
                    for(const concrete of response.body) concretes.push({value: concrete['_id'], label: concrete['name'], specifications: concrete['specifications'], specifications_maturity: concrete['specifications_maturity'], specifications_maturity_relative: concrete['specifications_maturity_relative'] });
                    const concreteField = field.parent.get('concrete');
                    concreteField.props.options = concretes;
                    concreteField.formControl.setValue(null);
                  },
                  error: (e) => {} // ADD TOAST e),
                });
                field.formControl.valueChanges.pipe(
                  map(value => {
                    if (value!=null) {
                      const concrete = (field.props.options as any[]).find(concrete => concrete.value === value);
                      field.parent.get('specifications').formControl.setValue(concrete.specifications);
                    }
                  })
                ).subscribe()
              }
            }
          },
          {
            key: 'specifications',
            type: 'chips-number',
            wrappers: ['default-wrapper'],
            props: {
              type: 'number',
              required: false,
              matSuffix: ' MPa'
            },
            expressions: {
              'props.label': translateService.stream('fields.specifications')
            }
          },
          {
            key: 'specifications_maturity',
            type: 'chips-number',
            wrappers: ['default-wrapper'],
            // hide: !config.visualizationConfig.maturity.app,
            props: {
              type: 'number',
              required: false,
              matSuffix: ' ºC d',
            },
            expressions: {
              'props.label': translateService.stream('fields.specifications_maturity'),
            }
          },
          {
            key: 'specifications_maturity_relative',
            type: 'chips-number',
            wrappers: ['default-wrapper'],
            // hide: !config.visualizationConfig.relativeMaturity.app,
            props: {
              type: 'number',
              required: false,
              matSuffix: ' ºC d'
            },
            expressions: {
              'props.label': translateService.stream('fields.specifications_maturity_relative')
            }
          },
        ],
        TemperatureMeasurement: [
          // {
          //   key: 'temp',
          //   type: 'input',
          //   props: {
          //     label: 'Temperature',
          //     placeholder: 'Temperature field',
          //     required: true
          //   },
          // }
        ],
        StructuralHealthMeasurement: [
          {
            key: 'dataChannels',
            type: 'shm-channels',
            defaultValue: [{ channelId: 1, type: 'None', parameters: {} }],
            props: { label: 'fields.dataChannels' },
            fieldArray: {
              fieldGroup: [
                {
                  key: 'channelId',
                  type: 'select',
                  props: {
                    label: translateService.instant('fields.channel'),
                    required: true,
                  },
                  wrappers: ['default-wrapper'],
                  expressions: {
                    'props.options': (field: any) => {
                      const parentArray = (field.parent?.parent?.model || []);
                      const rowModel = field.model;
                      if (!Array.isArray(parentArray)) return [];

                      const currentIndex = parentArray.indexOf(rowModel);
                      const usedChannels = parentArray
                        .map((item, idx) => (item && idx !== currentIndex) ? item.channelId : null)
                        .filter(id => id !== null);

                      return Array.from({ length: MAX_SHM_CHANNELS }, (_, i) => i + 1)
                        .filter(c => !usedChannels.includes(c) || c === rowModel?.channelId)
                        .map(c => ({
                          value: c,
                          label: `Channel ${c}`
                        }));
                    }
                  }
                },
                {
                  // Optional display label — charts / CSV fall back to the
                  // channel number when blank. Shown for every channel type.
                  key: 'name',
                  type: 'input',
                  props: { required: false },
                  expressions: {
                    'props.label': translateService.stream('fields.channelName'),
                  }
                },
                {
                  key: 'type',
                  type: 'select',
                  props: {
                    label: translateService.instant('fields.type'),
                    required: true,
                    options: SHM_CHANNEL_TYPES.map(t => ({ value: t.value, label: translateService.instant(t.label) }))
                  }
                },
                {
                  key: 'parameters.diameter',
                  type: 'select',
                  props: {
                    required: true,
                    options: diameterOptions
                  },
                  expressions: {
                    'props.label': translateService.stream('fields.diameter'),
                    hide: (field) => field.model?.type !== 'corrosion'
                  }
                },
                // Rebar cover + design strength — inputs to the thickloss
                // structural-limit threshold band. Optional: the band simply
                // isn't drawn when either is absent.
                {
                  key: 'parameters.cover',
                  type: 'input',
                  props: {
                    type: 'number',
                    required: false
                  },
                  expressions: {
                    'props.label': translateService.stream('fields.cover'),
                    hide: (field) => field.model?.type !== 'corrosion'
                  }
                },
                {
                  key: 'parameters.fck',
                  type: 'input',
                  props: {
                    type: 'number',
                    required: false
                  },
                  expressions: {
                    'props.label': translateService.stream('fields.fck'),
                    hide: (field) => field.model?.type !== 'corrosion'
                  }
                },
                {
                  key: 'parameters.length',
                  type: 'select',
                  props: {
                    required: true,
                    options: lengthOptions
                  },
                  expressions: {
                    'props.label': translateService.stream('fields.length'),
                    hide: (field) => field.model?.type !== 'corrosion'
                  }
                },
                {
                  key: 'parameters.diameterCustom',
                  type: 'input',
                  props: {
                    type: 'number',
                    required: true
                  },
                  expressions: {
                    'props.label': translateService.stream('fields.diameterCustom'),
                    hide: (field) => field.model?.type !== 'corrosion' || field.model?.parameters?.diameter !== 'custom'
                  }
                },
                {
                  key: 'parameters.lengthCustom',
                  type: 'input',
                  props: {
                    type: 'number',
                    required: true
                  },
                  expressions: {
                    'props.label': translateService.stream('fields.lengthCustom'),
                    hide: (field) => field.model?.type !== 'corrosion' || field.model?.parameters?.length !== 'custom'
                  }
                }
              ]
            }
          }
        ]
      }
    this.fields = this.commonFields;
  }
}

export class AdminEditMeasurementForm {
  commonFields: FormlyFieldConfig[];

  fields: FormlyFieldConfig[];
  model: {};
  actionText = 'button.save';

  constructor(translateService, measurement, stagesService, user, config?) {
    this.model = measurement;

    const isAdminUser = user?.roles?.includes('admin') || user?.roles?.includes('superadmin');
    const barDiameters: number[] = config?.deviceProfile?.parameters?.corrosion?.barDiameters ?? [];
    const barLengths: number[] = config?.deviceProfile?.parameters?.corrosion?.barLengths ?? [];
    const diameterOptions = [
      ...barDiameters.map(d => ({ value: d, label: `${d} mm` })),
      ...(isAdminUser ? [{ value: 'custom', label: translateService.instant('fields.custom') }] : [])
    ];
    const lengthOptions = [
      ...barLengths.map(l => ({ value: l, label: `${l} mm` })),
      ...(isAdminUser ? [{ value: 'custom', label: translateService.instant('fields.custom') }] : [])
    ];

    if (measurement.measurementType === 'StructuralHealthMeasurement') {
      if (this.model['dataChannels'] && typeof this.model['dataChannels'] === 'object' && !Array.isArray(this.model['dataChannels'])) {
        this.model['dataChannels'] = Object.keys(this.model['dataChannels']).map(key => {
          const channelId = parseInt(key.replace('channel', ''));
          return {
            channelId,
            ...this.model['dataChannels'][key]
          };
        });
      } else if (!this.model['dataChannels']) {
         this.model['dataChannels'] = [{ channelId: 1, type: 'None', parameters: {} }];
      }
    }
    this.commonFields = [
      {
        key: 'name',
        type: 'input',
        wrappers: ['default-wrapper'],
        defaultValue: measurement.name,
        props: {
          required: true
        },
        expressions: {
          'props.label': translateService.stream('fields.name')
        }
      },
      {
        key: 'description',
        type: 'input',
        wrappers: ['default-wrapper'],
        defaultValue: measurement.description,
        props: {
          required: false
        },
        expressions: {
          'props.label': translateService.stream('fields.description')
        }
      },
      {
        key: 'measurementType',
        type: 'select',
        wrappers: ['default-wrapper'],
        defaultValue: measurement.measurementType,
        props: {
          required: true,
          disabled: true,
          options: [{value: measurement.measurementType, label: translateService.instant('measurement.types.' + measurement.measurementType)}]
        },
        expressions: {
          'props.label': translateService.stream('fields.measurementType'),
        }
      },
      {
        key: 'started',
        type: 'checkbox',
        wrappers: ['checkbox-wrapper'],
        defaultValue: measurement.started !== false,
        hide: !(measurement.measurementType === 'MaturityMeasurement' || measurement.measurementType === 'TemperatureMeasurement'),
        props: {
          required: false,
        },
        expressions: {
          'props.label': translateService.stream('fields.started'),
        }
      },
      {
          key: 'project',
          type: 'select',
          wrappers: ['default-wrapper'],
          defaultValue: measurement.project._id,
          props: {
            required: true,
            disabled: true,
            options: [{value: measurement.project._id, label: measurement.project.name}]
          },
          expressions: {
            'props.label': translateService.stream('fields.project'),
          },
          hooks: {
            onInit: (field) => {
              const selectedProject = field.formControl.value;
              stagesService.find('?project='+ selectedProject).subscribe({
                next: (response) => {
                  const stages: {value: string, label: string}[] = [];
                  for(const stage of response.body) stages.push({value: stage['_id'], label: stage['name'] });
                  const stageField = field.parent.get('stage');
                  stageField.props.options = stages;
                  stageField.formControl.setValue(measurement.stage._id);
                },
                error: (e) => {} // ADD TOAST e),
              });
            }
          }
        },
      {
          key: 'stage',
          type: 'select',
          wrappers: ['default-wrapper'],
          props: {
            required: true,
          },
          expressions: {
            'props.label': translateService.stream('fields.stage'),
            hide: '!model.project'
          },
        },
      {
        key: 'specifications',
        type: 'chips-number',
        wrappers: ['default-wrapper'],
        defaultValue: measurement.specifications,
        hide: !(measurement.measurementType === 'MaturityMeasurement'),
        props: {
          type: 'number',
          required: false,
          matSuffix: ' MPa'
        },
        expressions: {
          'props.label': translateService.stream('fields.specifications')
        }
      },
      {
        key: 'specifications_maturity',
        type: 'chips-number',
        wrappers: ['default-wrapper'],
        defaultValue: measurement.specifications_maturity,
        hide: !(measurement.measurementType === 'MaturityMeasurement'),
        props: {
          type: 'number',
          required: false,
          matSuffix: ' ºC d',
        },
        expressions: {
          'props.label': translateService.stream('fields.specifications_maturity'),
        }
      },
      {
        key: 'specifications_maturity_relative',
        type: 'chips-number',
        wrappers: ['default-wrapper'],
        defaultValue: measurement.specifications_maturity_relative,
        hide: !(measurement.measurementType === 'MaturityMeasurement'),
        props: {
          type: 'number',
          required: false,
          matSuffix: ' ºC d'
        },
        expressions: {
          'props.label': translateService.stream('fields.specifications_maturity_relative')
        }
      },
      {
        key: 'dataChannels',
        type: 'shm-channels',
        defaultValue: (measurement.dataChannels && measurement.dataChannels.length > 0)
          ? measurement.dataChannels
          : [{ channelId: 1, type: 'None', parameters: {} }],
        hide: !(measurement.measurementType === 'StructuralHealthMeasurement'),
        props: { label: translateService.instant('fields.dataChannels') },
        fieldArray: {
          fieldGroup: [
            {
              key: 'channelId',
              type: 'select',
              props: {
                label: translateService.instant('fields.channel'),
                required: true,
              },
              wrappers: ['default-wrapper'],
              expressions: {
                'props.options': (field: any) => {
                  const parentArray = (field.parent?.parent?.model || []);
                  const rowModel = field.model;
                  if (!Array.isArray(parentArray)) return [];

                  const currentIndex = parentArray.indexOf(rowModel);
                  const usedChannels = parentArray
                    .map((item, idx) => (item && idx !== currentIndex) ? item.channelId : null)
                    .filter(id => id !== null);

                  return Array.from({ length: MAX_SHM_CHANNELS }, (_, i) => i + 1)
                    .filter(c => !usedChannels.includes(c) || c === rowModel?.channelId)
                    .map(c => ({
                      value: c,
                      label: `Channel ${c}`
                    }));
                }
              }
            },
            {
              // Optional display label — charts / CSV fall back to the channel
              // number when blank. Shown for every channel type.
              key: 'name',
              type: 'input',
              props: { required: false },
              expressions: {
                'props.label': translateService.stream('fields.channelName'),
              }
            },
            {
              key: 'type',
              type: 'select',
              props: {
                label: translateService.instant('fields.type'),
                required: true,
                options: SHM_CHANNEL_TYPES.map(t => ({ value: t.value, label: translateService.instant(t.label) }))
              }
            },
            {
              key: 'parameters.diameter',
              type: 'select',
              props: {
                required: true,
                options: diameterOptions
              },
              expressions: {
                'props.label': translateService.stream('fields.diameter'),
                hide: (field) => field.model?.type !== 'corrosion'
              }
            },
            // Rebar cover + design strength — inputs to the thickloss
            // structural-limit threshold band. Optional: the band simply
            // isn't drawn when either is absent.
            {
              key: 'parameters.cover',
              type: 'input',
              props: {
                type: 'number',
                required: false
              },
              expressions: {
                'props.label': translateService.stream('fields.cover'),
                hide: (field) => field.model?.type !== 'corrosion'
              }
            },
            {
              key: 'parameters.fck',
              type: 'input',
              props: {
                type: 'number',
                required: false
              },
              expressions: {
                'props.label': translateService.stream('fields.fck'),
                hide: (field) => field.model?.type !== 'corrosion'
              }
            },
            {
              key: 'parameters.length',
              type: 'select',
              props: {
                required: true,
                options: lengthOptions
              },
              expressions: {
                'props.label': translateService.stream('fields.length'),
                hide: (field) => field.model?.type !== 'corrosion'
              }
            },
            {
              key: 'parameters.diameterCustom',
              type: 'input',
              props: {
                type: 'number',
                required: true
              },
              expressions: {
                'props.label': translateService.stream('fields.diameterCustom'),
                hide: (field) => field.model?.type !== 'corrosion' || field.model?.parameters?.diameter !== 'custom'
              }
            },
            {
              key: 'parameters.lengthCustom',
              type: 'input',
              props: {
                type: 'number',
                required: true
              },
              expressions: {
                'props.label': translateService.stream('fields.lengthCustom'),
                hide: (field) => field.model?.type !== 'corrosion' || field.model?.parameters?.length !== 'custom'
              }
            }
          ]
        }
      },
    ];
    this.fields = this.commonFields;
  }
}
